import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import Stars from '@/images/Stars.svg';
import theme from '@/theme';
import { isV2Presenter, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { AvatarTag } from './AvatarTag';

const Plus = styled.span`
    color: #ff882e;
    font-size: 14px;
    font-weight: 800;
`;

const EmptyAvatarTag = styled.div`
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
`;

const ExpressiveIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    background-color: #d4e7ff;
    opacity: 0.85;
    border-radius: 4px;
    backdrop-filter: blur(10px);

    svg {
        margin: 0;
    }
`;

interface Props {
    type: VideoType | string;
    avatarId?: string;
    className?: string;
    isAiGenerated?: boolean;
    showExpressiveTag?: boolean;
}

export type AvatarType = 'premium_plus_ai' | 'premium_plus' | 'premium' | 'express' | 'standard';

const getAvatarType = (type: string, avatarId?: string, isAiGenerated?: boolean): AvatarType => {
    switch (type) {
        case VideoType.CLIP:
            return isV2Presenter(avatarId || '') ? (!!isAiGenerated ? 'premium_plus_ai' : 'premium_plus') : 'premium';
        case VideoType.TRAIN:
            return 'premium_plus';
        case VideoType.Scene:
            return 'express';
        default:
            return 'standard';
    }
};

const getAvatarTypeContent = (type: AvatarType) => {
    switch (type) {
        case 'premium_plus_ai':
            return (
                <>
                    Premium<Plus>+</Plus> AI
                </>
            );
        case 'premium_plus':
            return (
                <>
                    Premium<Plus>+</Plus>
                </>
            );
        case 'premium':
            return <>Premium</>;
        case 'express':
            return <>Express</>;
        case 'standard':
            return null;
        default:
            return null;
    }
};

export const AvatarTypeTag = ({ type, avatarId, className, isAiGenerated, showExpressiveTag }: Props) => {
    const avatarType = getAvatarType(type, avatarId, isAiGenerated);
    const content = getAvatarTypeContent(avatarType);

    if (type === VideoType.EXPRESSIVE && showExpressiveTag) {
        return (
            <CustomTooltip
                placement="top"
                title="This avatar shifts expressions automatically to match the conversation."
                slotProps={{
                    tooltip: {
                        sx: {
                            maxWidth: '344px !important',
                            padding: '16px',
                        },
                    },
                }}>
                <ExpressiveIconWrapper>
                    <MaskedIcon src={Stars} size="14px" color={theme.colors.links.main} />
                </ExpressiveIconWrapper>
            </CustomTooltip>
        );
    }

    if (!content && avatarId) {
        return <EmptyAvatarTag data-testid={`avatar-tag-${avatarType}`} />;
    }

    if (content) {
        return (
            <AvatarTag type={avatarType} className={className}>
                {content}
            </AvatarTag>
        );
    }

    return null;
};
